import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Plus, Trash2, Save, Wallet } from "lucide-react";

const COLORS = ["#38BDF8", "#10B981", "#818CF8", "#F59E0B", "#EC4899", "#22D3EE", "#F472B6"];

export default function AdminPanel({ open, onOpenChange }) {
  const [budget, setBudget] = useState(null);
  const [services, setServices] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState(0);
  const [google, setGoogle] = useState(0);
  const [svcAlloc, setSvcAlloc] = useState({});
  const [newSvc, setNewSvc] = useState({ name: "", target_cpl: "", avg_ticket_size: "", close_rate: "" });
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [b, s] = await Promise.all([api.get("/budgets"), api.get("/services")]);
    setBudget(b.data); setServices(s.data);
    setTotal(b.data.total_budget || 0);
    setMeta(b.data.platform_allocations?.meta || 0);
    setGoogle(b.data.platform_allocations?.google || 0);
    setSvcAlloc(b.data.service_allocations || {});
  };

  useEffect(() => { if (open) loadAll(); }, [open]);

  const saveBudget = async () => {
    setSaving(true);
    try {
      const service_allocations = {};
      services.forEach((s) => { service_allocations[s.name] = Number(svcAlloc[s.name] || 0); });
      await api.put("/budgets", {
        total_budget: Number(total),
        platform_allocations: { meta: Number(meta), google: Number(google) },
        service_allocations,
      });
      toast.success("Allocation saved");
      window.dispatchEvent(new Event("dash-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const addService = async () => {
    if (!newSvc.name.trim()) return toast.error("Enter a service name");
    try {
      await api.post("/services", {
        name: newSvc.name, target_cpl: Number(newSvc.target_cpl || 0),
        avg_ticket_size: Number(newSvc.avg_ticket_size || 0),
        close_rate: Number(newSvc.close_rate || 0.15),
        color: COLORS[services.length % COLORS.length],
      });
      toast.success("Service added");
      setNewSvc({ name: "", target_cpl: "", avg_ticket_size: "", close_rate: "" });
      await loadAll(); window.dispatchEvent(new Event("dash-refresh"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateService = async (s) => {
    try {
      await api.put(`/services/${s.id}`, {
        name: s.name, target_cpl: Number(s.target_cpl),
        avg_ticket_size: Number(s.avg_ticket_size), close_rate: Number(s.close_rate), color: s.color,
      });
      toast.success("Service updated");
      window.dispatchEvent(new Event("dash-refresh"));
    } catch (e) { toast.error("Failed"); }
  };

  const deleteService = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Removed"); await loadAll(); window.dispatchEvent(new Event("dash-refresh")); }
    catch (e) { toast.error("Failed"); }
  };

  const inp = "bg-[#0F172A] border-white/10 text-white h-10 focus-visible:ring-[#38BDF8]";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="admin-panel"
        className="bg-[#0F172A] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white font-display flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#38BDF8]" /> Budget & Services
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Configure the platform/service allocation plan and manage service lines.
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="budget" className="mt-6">
          <TabsList className="bg-[#1E293B] border border-white/10 w-full">
            <TabsTrigger value="budget" data-testid="admin-tab-budget" className="flex-1 data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Allocation</TabsTrigger>
            <TabsTrigger value="services" data-testid="admin-tab-services" className="flex-1 data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Services</TabsTrigger>
          </TabsList>

          <TabsContent value="budget" className="space-y-4 mt-5">
            <p className="text-xs text-slate-400">This is the platform/service split plan. Per-branch total budgets are set on the Branches page.</p>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Plan Total (₹)</Label>
              <Input data-testid="budget-total-input" type="number" value={total} onChange={(e) => setTotal(e.target.value)} className={inp} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Meta (₹)</Label>
                <Input data-testid="budget-meta-input" type="number" value={meta} onChange={(e) => setMeta(e.target.value)} className={inp} />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Google (₹)</Label>
                <Input data-testid="budget-google-input" type="number" value={google} onChange={(e) => setGoogle(e.target.value)} className={inp} />
              </div>
            </div>
            <div className="space-y-2 pt-2">
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Per-Service Allocation (₹)</Label>
              {services.map((s) => (
                <div key={s.id} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="text-sm text-slate-300 flex-1 truncate">{s.name}</span>
                  <Input type="number" value={svcAlloc[s.name] || 0}
                    onChange={(e) => setSvcAlloc({ ...svcAlloc, [s.name]: e.target.value })}
                    className={`${inp} w-32`} />
                </div>
              ))}
            </div>
            <Button data-testid="save-budget-btn" onClick={saveBudget} disabled={saving}
              className="w-full h-11 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Allocation"}
            </Button>
          </TabsContent>

          <TabsContent value="services" className="space-y-4 mt-5">
            <div className="rounded-lg bg-[#1E293B] border border-white/10 p-4 space-y-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Add Service Line</p>
              <Input data-testid="new-service-name" placeholder="Service name (e.g. Tattoo Removal)" value={newSvc.name}
                onChange={(e) => setNewSvc({ ...newSvc, name: e.target.value })} className={inp} />
              <div className="grid grid-cols-3 gap-2">
                <Input data-testid="new-service-cpl" type="number" placeholder="Target CPL" value={newSvc.target_cpl}
                  onChange={(e) => setNewSvc({ ...newSvc, target_cpl: e.target.value })} className={inp} />
                <Input data-testid="new-service-ticket" type="number" placeholder="Ticket ₹" value={newSvc.avg_ticket_size}
                  onChange={(e) => setNewSvc({ ...newSvc, avg_ticket_size: e.target.value })} className={inp} />
                <Input data-testid="new-service-close" type="number" step="0.01" placeholder="Close %" value={newSvc.close_rate}
                  onChange={(e) => setNewSvc({ ...newSvc, close_rate: e.target.value })} className={inp} />
              </div>
              <Button data-testid="add-service-btn" onClick={addService}
                className="w-full bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-semibold rounded-full">
                <Plus className="w-4 h-4 mr-1" /> Add Service
              </Button>
            </div>

            <div className="space-y-2">
              {services.map((s, idx) => (
                <div key={s.id} className="rounded-lg bg-[#1E293B] border border-white/10 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                    <Input value={s.name} onChange={(e) => { const n = [...services]; n[idx] = { ...s, name: e.target.value }; setServices(n); }} className={`${inp} flex-1 h-9`} />
                    <Button data-testid={`delete-service-${idx}`} onClick={() => deleteService(s.id)} className="h-9 w-9 p-0 bg-[#EF4444]/15 hover:bg-[#EF4444]/25 text-[#EF4444] rounded-lg"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><span className="text-[10px] text-slate-500 uppercase">Target CPL</span>
                      <Input type="number" value={s.target_cpl} onChange={(e) => { const n = [...services]; n[idx] = { ...s, target_cpl: e.target.value }; setServices(n); }} className={`${inp} h-9`} /></div>
                    <div><span className="text-[10px] text-slate-500 uppercase">Ticket ₹</span>
                      <Input type="number" value={s.avg_ticket_size} onChange={(e) => { const n = [...services]; n[idx] = { ...s, avg_ticket_size: e.target.value }; setServices(n); }} className={`${inp} h-9`} /></div>
                    <div><span className="text-[10px] text-slate-500 uppercase">Close rate</span>
                      <Input type="number" step="0.01" value={s.close_rate} onChange={(e) => { const n = [...services]; n[idx] = { ...s, close_rate: e.target.value }; setServices(n); }} className={`${inp} h-9`} /></div>
                  </div>
                  <Button data-testid={`save-service-${idx}`} onClick={() => updateService(services[idx])} className="w-full h-8 bg-[#0F172A] hover:bg-[#334155] border border-white/10 text-slate-300 rounded-lg text-xs"><Save className="w-3.5 h-3.5 mr-1" /> Update</Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
